import { Service } from '@genial/services';
import { DefaultValue, selector, selectorFamily } from 'recoil';

import { sessionState } from '../session/atom';

import { masterState } from './atom';
import { normalizeAdvisors, normalizePartners } from './normalize';
import { MasterStateProps } from './types';

export const masterStateSelector = selectorFamily<
  MasterStateProps | undefined,
  { isMaster: boolean }
>({
  key: 'masterState/Selector',
  get:
    ({ isMaster }) =>
    async ({ get }) => {
      if (!isMaster) {
        return {
          advisorList: [],
          partnerList: [],
          selectedAdvisor: undefined,
          selectedAdvisorData: undefined,
          selectedPartner: undefined,
          selectedPartnerData: undefined,
          selectedAdvisorName: undefined,
          loaded: true,
        };
      }

      const { userProfile } = get(sessionState);

      const { data } = await Service.Advisor.V1.getGroupId({ screenName: 'advisor' });
      const filteredData = data?.advisor?.filter((advisor) => {
        return advisor.partnerId !== userProfile?.idAssessorIntranet;
      });

      const partnerListNormalized = normalizePartners(data?.partner, data.isMasterGroup);
      const advisorListNormalized = normalizeAdvisors(
        filteredData,
        partnerListNormalized,
        data.isMasterPartner,
        data.isMasterGroup
      );

      return {
        loaded: true,
        advisorList: advisorListNormalized,
        partnerList: partnerListNormalized,
        selectedPartner: userProfile?.idParceiro,
        selectedPartnerData: partnerListNormalized.find(
          (partner) => partner.id === userProfile?.idParceiro
        ),
        selectedAdvisor: undefined,
        selectedAdvisorData: undefined,
      };
    },
});

export const masterStateAdvisorListSelector = selector<MasterStateProps['advisorList']>({
  key: 'masterState/AdvisorListSelector',
  get: ({ get }) => {
    return get(masterState).advisorList;
  },
  set: ({ set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      return;
    }

    set(masterState, (prevState) => ({
      ...prevState,
      advisorList: newValue,
    }));
  },
});

export const masterStateSelectedAdvisorSelector = selector<MasterStateProps['selectedAdvisor']>({
  key: 'masterState/AdvisorSelectedSelector',
  get: ({ get }) => {
    return get(masterState).selectedAdvisor;
  },
  set: ({ set, get }, newValue) => {
    if (newValue instanceof DefaultValue) {
      return;
    }

    const masterStateData = get(masterState);

    const selectedAdvisor = masterStateData.advisorList.find(
      (advisor) => advisor.id === newValue && advisor.partnerId === masterStateData.selectedPartner
    );

    set(masterState, (prevState) => ({
      ...prevState,
      selectedAdvisor: newValue,
      selectedAdvisorData: selectedAdvisor ? { ...selectedAdvisor } : undefined,
      selectedAdvisorName: selectedAdvisor?.name,
    }));
  },
});

export const masterStateSelectedPartnerSelector = selector<MasterStateProps['selectedPartner']>({
  key: 'masterState/PartnerSelectedSelector',
  get: ({ get }) => {
    return get(masterState).selectedPartner;
  },
  set: ({ set, get }, newValue) => {
    if (newValue instanceof DefaultValue) {
      return;
    }

    set(masterState, (prevState) => ({
      ...prevState,
      selectedPartner: newValue,
      selectedPartnerData: get(masterState).partnerList.find((partner) => partner.id === newValue),
    }));
  },
});

export const masterStateSelectedAdvisorNameSelector = selector<
  MasterStateProps['selectedAdvisorName']
>({
  key: 'masterState/AdvisorSelectedSelectorName',
  get: ({ get }) => {
    return get(masterState).selectedAdvisorName;
  },
});

export const masterStateSelectedAdvisorDataSelector = selector<
  MasterStateProps['selectedAdvisorData']
>({
  key: 'masterState/AdvisorSelectedDataSelector',
  get: ({ get }) => {
    return get(masterState).selectedAdvisorData;
  },
});

export const masterStateAdvisorListLoaded = selector<MasterStateProps['loaded']>({
  key: 'masterState/AdvisorListLoadedSelector',
  set: ({ set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      return;
    }
    set(masterState, (prevState) => ({
      ...prevState,
      loaded: newValue,
    }));
  },
  get: ({ get }) => {
    return get(masterState).loaded;
  },
});

export const masterStateSelectedAdvisorGroupSelector = selector<
  MasterStateProps['selectedAdvisorGroup']
>({
  key: 'masterState/SelectedAdvisorGroupSelector',
  get: ({ get }) => {
    return get(masterState).selectedAdvisorGroup;
  },
  set: ({ set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      return;
    }
    set(masterState, (prevState) => ({
      ...prevState,
      selectedAdvisorGroup: newValue,
    }));
  },
});

export const advisorIdToApisSelector = selector<number>({
  key: 'masterState/AdvisorIdToApisSelector',
  get: ({ get }) => {
    const { userProfile } = get(sessionState);
    const advisorSelected = get(masterStateSelectedAdvisorSelector);
    const advisorIdToUse = advisorSelected ?? userProfile?.idAssessorIntranet;

    return advisorIdToUse ?? 0;
  },
});

export const partnerIdToApisSelector = selector<number | undefined>({
  key: 'masterState/PartnerIdToApisSelector',
  get: ({ get }) => {
    const advisorSelected = get(masterStateSelectedAdvisorSelector);
    const partnerSelected = get(masterStateSelectedPartnerSelector);

    return advisorSelected !== undefined ? partnerSelected : undefined;
  },
});

export const branchIdToApisSelector = selector<number>({
  key: 'masterState/BranchIdToApisSelector',
  get: ({ get }) => {
    const { userProfile } = get(sessionState);
    const advisorData = get(masterStateSelectedAdvisorDataSelector);

    return advisorData?.branchId ?? userProfile?.idFilialParceiroIntranet ?? 0;
  },
});
